import { getIdToken } from '@modules/firebase/auth';
import { getInterviewsForUser } from '@modules/firebase/interviews';
import { breadcrumb, log, logError } from '@modules/logging';
import { Interview } from '@synergetx/puppet-interview-types';
import { useDatabaseUser } from '@components/FirebaseAuthentication';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLatest } from 'react-use';
import { readSelectedInterview, writeSelectedInterview } from '@modules/selected-interview';
import invariant from 'tiny-invariant';

export interface InterviewOptionsContextValues {
    addAndSelectNewInterview: {
        readonly current: (newInterview: Interview) => void;
    };
    allGroups: string[];
    allInterviews: Interview[] | undefined;
    availableInterviews: Interview[] | undefined;
    archivedInterviews: Interview[] | undefined;
    loading: boolean;
    fetchInterviews: () => Promise<void>;
    selectInterview: (id: string, groupId: string) => void;
    selectedInterview: Interview | undefined;
}

export const InterviewOptionsContext = createContext<InterviewOptionsContextValues | null>(null);

export const InterviewsProvider = ({
    children,
}: Record<string, unknown> & {
    children?: React.ReactNode;
}) => {
    const [allInterviews, setAllInterviews] = useState<Interview[]>();
    const [selectedInterview, setSelectedInterview] = useState<Interview>();
    const [loading, setLoading] = useState<boolean>(true);
    const databaseUser = useDatabaseUser();
    // Read once at mount. localStorage is synchronous, so unlike the redux-remember rehydrate
    // this cannot arrive after the routes have already been registered without a selection.
    const persistedInterview = useMemo(() => readSelectedInterview(), []);

    const { archived: archivedInterviews, available: availableInterviews } = useMemo(() => {
        if (!allInterviews) return { archived: [], available: [] };
        const archived = allInterviews.filter((i) => i.archived);
        const available = allInterviews.filter((i) => !i.archived);
        return { archived, available };
    }, [allInterviews]);

    const updateSelectedInterviewRef = useLatest((selectedId: string, selectedGroupId: string) => {
        const availableInterviewFound = allInterviews?.find(
            ({ id, groupId }) => id === selectedId && groupId === selectedGroupId,
        );
        if (availableInterviewFound) {
            const { id, name, groupId } = availableInterviewFound;
            writeSelectedInterview({ id, name, groupId });

            setSelectedInterview(availableInterviewFound);
        } else {
            log(`tried to set interview that wasn't available`, {
                allInterviews,
                databaseUser,
                persistedInterview,
                selectedGroupId,
                selectedId,
            });
        }
    });

    // Stable wrapper so the ref is read at call time rather than during render.
    const updateSelectedInterview = useCallback(
        (selectedId: string, selectedGroupId: string) =>
            updateSelectedInterviewRef.current(selectedId, selectedGroupId),
        [updateSelectedInterviewRef],
    );

    // select newly created interview - before allInterviews is updated
    const addAndSelectNewInterview = useLatest((newInterview: Interview) => {
        const { id, name, groupId } = newInterview;
        writeSelectedInterview({ id, name, groupId });
        setAllInterviews((prev) => (prev ? [...prev, newInterview] : [newInterview]));
    });

    const allGroups = useMemo(() => {
        const groupSet = new Set<string>();
        allInterviews?.forEach(({ groupId }) => groupSet.add(groupId));
        return Array.from(groupSet);
    }, [allInterviews]);

    // When the user & persisted selected interview have loaded,
    // find and select the last interview they selected
    useEffect(() => {
        // only restore if what was persisted differs from what is already selected in state

        // if nothing was persisted, quit now
        if (!persistedInterview?.groupId || !persistedInterview?.id) return;

        // do nothing if we're still loading
        if (loading || !allInterviews?.length) return;

        const availableInterviewFound = allInterviews?.find(
            ({ id, groupId }) =>
                id === persistedInterview.id && groupId === persistedInterview.groupId,
        );

        if (availableInterviewFound) {
            updateSelectedInterview(persistedInterview.id, persistedInterview.groupId);
        } else {
            // This will likely happen when we first roll out this version, but should drop off after april 2024
            log(`User's selected interview not available`, {
                persistedInterviewId: persistedInterview.id,
                persistedInterviewGroup: persistedInterview.groupId,
                selectedInterviewId: selectedInterview?.groupId,
                selectedInterviewGroup: selectedInterview?.groupId,
                allInterviews: allInterviews?.map(({ id, groupId }) => ({ id, groupId })),
            });
        }
        // Warning to all: don't include a reference from useLatest in your dependency array, it does not make sense...
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        allInterviews,
        loading,
        // selectInterview,
        selectedInterview?.groupId,
        selectedInterview?.id,
        persistedInterview?.id,
        persistedInterview?.groupId,
    ]);

    const fetchInterviewsRef = useLatest(async () => {
        setLoading(true);

        let interviews: Interview[] = [];
        try {
            if (!databaseUser?.availableInterviews?.length) {
                breadcrumb(`missing users or no available interviews:`, 'interviews', {
                    availableInterviews: databaseUser?.availableInterviews,
                });
            } else {
                // refresh auth token
                await getIdToken();
                interviews = await getInterviewsForUser(databaseUser.availableInterviews);
            }
        } catch (error) {
            logError(error as Error);
        } finally {
            // All in `finally`: `loading` gates the app shell, so a throw here must still clear it.
            setAllInterviews(interviews);
            // Match groupId too — ids come from names, so "Demo" can exist in two groups.
            setSelectedInterview(
                interviews.find(
                    ({ id, groupId }) =>
                        id === selectedInterview?.id && groupId === selectedInterview?.groupId,
                ),
            );
            setLoading(false);
        }
    });

    // Stable wrapper so the ref is read at call time rather than during render.
    const fetchInterviews = useCallback(() => fetchInterviewsRef.current(), [fetchInterviewsRef]);

    // if the user has interviews available, get their details
    useEffect(() => {
        fetchInterviews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [databaseUser?.availableInterviews?.length]);

    const contextValue: InterviewOptionsContextValues = useMemo(
        () => ({
            addAndSelectNewInterview,
            allInterviews,
            allGroups,
            archivedInterviews,
            availableInterviews,
            fetchInterviews,
            loading,
            selectInterview: updateSelectedInterview,
            selectedInterview,
        }),
        [
            addAndSelectNewInterview,
            allInterviews,
            allGroups,
            archivedInterviews,
            availableInterviews,
            fetchInterviews,
            loading,
            selectedInterview,
            updateSelectedInterview,
        ],
    );

    return <InterviewOptionsContext value={contextValue}>{children}</InterviewOptionsContext>;
};

export const useInterviewOptions = () => {
    const ctx = useContext(InterviewOptionsContext);

    invariant(ctx, 'useInterviewOptions called outside of InterviewOptionsContext');

    return ctx;
};
